import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { loadGoogleMaps } from "../MapPreview";
import { carIcon } from "../gps/carIcon";
import { MapPin, ChevronRight, MapPinOff } from "lucide-react";

const PIN = { live: "#38BDF8", stale: "#F59E0B", offline: "#64748B" };
const LBL = { live: ["LIVE", "text-sky-300"], stale: ["Non aggiornata", "text-amber-300"], offline: ["Offline", "text-slate-400"],
  waiting: ["In attesa GPS", "text-slate-400"], denied: ["Permesso negato", "text-rose-300"], unavailable: ["GPS non disponibile", "text-orange-300"] };
const STYLE = [
  { elementType: "geometry", stylers: [{ color: "#0B1220" }] }, { elementType: "labels.text.fill", stylers: [{ color: "#7C8BA1" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#05080F" }] }, { featureType: "road", elementType: "geometry", stylers: [{ color: "#18243A" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#040A14" }] }, { featureType: "poi", stylers: [{ visibility: "off" }] },
];

function DriverLine({ d, onOpen }) {
  const [lab, cls] = LBL[d.status] || ["—", "text-slate-400"];
  const nb = d.current_booking || d.next_booking;
  return (
    <button type="button" onClick={onOpen} data-testid={`dash-gps-driver-${d.driver_id}`} className="w-full text-left px-4 py-2.5 hover:bg-[#D4A72C]/5 flex items-center gap-3 min-h-[52px]">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PIN[d.status] || "#475569" }} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white truncate">{d.driver_name}<span className="text-slate-500 text-xs"> · {d.vehicle_plate || "—"}</span></div>
        <div className={`text-[11px] ${cls}`}>{lab}{d.silent_min ? ` · senza GPS da ${d.silent_min} min` : ""}</div>
      </div>
      <div className="text-right min-w-0 max-w-[45%]">
        <div className="text-[10px] text-slate-500">{d.current_booking ? "In corso" : "Prossimo servizio"}</div>
        <div className="text-xs text-slate-200 truncate">{nb ? `${nb.time || ""} ${nb.pickup || ""} → ${nb.destination || ""}` : "Nessuno"}</div>
      </div>
      <ChevronRight size={14} className="text-slate-600 flex-shrink-0" />
    </button>
  );
}

export function DashboardGpsMap({ title = "GPS LIVE" }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const el = useRef(null), map = useRef(null), markers = useRef({});

  useEffect(() => {
    const load = () => api.get("/gps/live").then(r => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    loadGoogleMaps(process.env.REACT_APP_GOOGLE_MAPS_API_KEY).then(g => {
      map.current = new g.Map(el.current, { center: { lat: 41.9028, lng: 12.4964 }, zoom: 10, styles: STYLE, disableDefaultUI: true, zoomControl: true, backgroundColor: "#0B1220" });
      load();
    }).catch(e => setErr(e.message));
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const g = window.google?.maps;
    if (!g || !map.current || !data) return;
    const seen = new Set();
    data.drivers.filter(d => d.position && PIN[d.status]).forEach(d => {
      seen.add(d.driver_id);
      const pos = { lat: d.position.lat, lng: d.position.lng };
      const icon = carIcon(g, PIN[d.status], d.position.heading, 32);
      const label = { text: `${(d.driver_name || "").split(" ")[0]} · ${d.vehicle_plate || ""}`, color: "#E0F2FE", fontSize: "10px", fontWeight: "700" };
      let m = markers.current[d.driver_id];
      if (!m) { m = new g.Marker({ map: map.current, position: pos, icon, label, title: `${d.driver_name || ""} · ${d.vehicle_plate || ""}`, optimized: false }); m.addListener("click", () => nav(`/gps-live?driver=${d.driver_id}`)); markers.current[d.driver_id] = m; }
      else { m.setPosition(pos); m.setIcon(icon); m.setLabel(label); }
    });
    Object.keys(markers.current).forEach(id => { if (!seen.has(id)) { markers.current[id].setMap(null); delete markers.current[id]; } });
  }, [data, nav]);

  const onShift = (data?.drivers || []).filter(d => !["not_started", "ended"].includes(d.status));
  const silent = onShift.filter(d => d.silent_min).length;
  const n = (s) => onShift.filter(d => d.status === s).length;
  const nOther = onShift.length - n("live") - n("stale") - n("offline");
  return (
    <div className="exec-card overflow-hidden min-w-0" data-testid="dash-gps-map">
      <div className="px-4 sm:px-5 py-3.5 border-b border-[#D4A72C]/12 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0"><MapPin size={18} className="text-[#E9C46A] flex-shrink-0" /><h2 className="font-display text-base md:text-lg font-bold text-white">{title}</h2>
          {data && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-bold" data-testid="dash-gps-updated"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />agg. {new Date(data.server_time).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>}</div>
        <button type="button" onClick={() => nav("/gps-live")} className="text-xs text-[#E9C46A] hover:text-[#FFE08A] font-semibold flex items-center gap-0.5 whitespace-nowrap min-h-[36px]" data-testid="dash-gps-open">Vedi mappa<ChevronRight size={13} /></button>
      </div>
      {silent > 0 && (
        <div className="px-5 py-2 bg-rose-500/10 border-b border-rose-500/30 text-rose-200 text-xs flex items-center gap-2" data-testid="dash-gps-silent">
          <MapPinOff size={14} />{silent} {silent === 1 ? "autista in turno è" : "autisti in turno sono"} senza GPS da oltre 10 minuti
        </div>
      )}
      <div className="relative h-[220px] sm:h-[260px] bg-[#0B1220]">
        <div ref={el} className="absolute inset-0" />
        {err && <div className="absolute inset-0 flex items-center justify-center text-sm text-rose-300">Mappa non disponibile</div>}
      </div>
      <div className="px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-y border-white/5 text-[11px]" data-testid="dash-gps-counts">
        <span className="uppercase tracking-widest text-[#E9C46A] font-semibold">Autisti in turno ({onShift.length})</span>
        <span className="text-sky-300">● {n("live")} online</span><span className="text-amber-300">● {n("stale")} non aggiornati</span>
        <span className="text-slate-400">● {n("offline")} offline</span>{nOther > 0 && <span className="text-slate-500">● {nOther} senza posizione</span>}
      </div>
      <div className="divide-y divide-white/5 max-h-[260px] overflow-y-auto">
        {onShift.map(d => <DriverLine key={d.driver_id} d={d} onOpen={() => nav(d.current_booking || d.next_booking ? `/prenotazioni?open=${(d.current_booking || d.next_booking).id}` : "/gps-live")} />)}
        {onShift.length === 0 && <div className="text-slate-500 text-sm text-center py-8" data-testid="dash-gps-empty">Nessun autista in turno con GPS</div>}
      </div>
    </div>
  );
}
